import React from 'react';
import { Link } from 'react-router-dom';
import { Search, Users, Activity } from 'lucide-react';

const GuideSection = ({ icon, title, children }) => (
    <div className="glass-card p-8 rounded-2xl mb-8">
        <div className="flex items-center text-cyan-400 mb-4">
            {icon}
            <h2 className="ml-4 text-3xl font-bold text-white">{title}</h2>
        </div>
        <div className="text-gray-300 leading-relaxed space-y-4">
            {children}
        </div>
    </div>
);

export default function OnChainAnalysisPage() {
    return (
        <div className="space-y-8">
            <div className="text-center">
                <h1 className="text-4xl md:text-5xl font-black mb-4 bg-gradient-to-r from-cyan-400 to-purple-400 bg-clip-text text-transparent">
                   Introduction to On-Chain Analysis
                </h1>
                <p className="text-lg text-gray-300 max-w-3xl mx-auto">
                    On-chain data is public information on the blockchain. Learning to read it helps you verify claims and understand a project's true health.
                </p>
            </div>

            <GuideSection icon={<Search size={28}/>} title="1. Use a Block Explorer">
                <p>A block explorer is your window into a blockchain. It lets you see all transactions, wallets, and smart contracts. Popular explorers include:</p>
                <ul className="list-disc list-inside space-y-2 pl-4">
                    <li><strong>Etherscan:</strong> For Ethereum and many EVM-compatible chains.</li>
                    <li><strong>BscScan:</strong> For BNB Smart Chain.</li>
                    <li><strong>Solscan:</strong> For the Solana network.</li>
                </ul>
                <p>Simply paste a transaction hash or wallet address into the search bar to get started.</p>
            </GuideSection>

            <GuideSection icon={<Users size={28}/>} title="2. Analyze Token Holder Distribution">
                <p>This is one of the most powerful tools for assessing risk. On a token's page in a block explorer, look for a "Holders" tab. Key things to check for:</p>
                 <ul className="list-disc list-inside space-y-2 pl-4">
                    <li><strong>Concentration Risk:</strong> Do a few wallets hold a huge percentage (e.g., >50%) of the total supply? These "whales" can crash the price by selling their tokens.</li>
                    <li><strong>"Rug Pull" Potential:</strong> If the top holders are non-contract wallets (not locked in a vesting contract or multisig), it could be a sign that the team can dump their tokens and abandon the project.</li>
                    <li><strong>Exchange Balances:</strong> Seeing large amounts of a token on major exchanges is generally a positive sign of liquidity and legitimacy.</li>
                </ul>
            </GuideSection>

            <GuideSection icon={<Activity size={28}/>} title="3. Monitor Transaction Activity">
                 <p>Look at the recent transaction history for a token. It tells a story.</p>
                 <ul className="list-disc list-inside space-y-2 pl-4">
                    <li><strong>Volume:</strong> Is there a healthy and consistent amount of trading volume, or just a few large, suspicious transactions?</li>
                    <li><strong>Whale Watching:</strong> Are the largest holders accumulating more tokens or selling them off? Large, frequent sell-offs can signal a price drop.</li>
                     <li><strong>Smart Contract Interactions:</strong> Are users actually interacting with the project's smart contracts for its intended purpose (e.g., staking, swapping)?</li>
                </ul>
            </GuideSection>

             <div className="text-center pt-6">
                <Link to="/learn" className="text-cyan-400 hover:text-cyan-300 font-semibold">
                    &larr; Back to All Learning Guides
                </Link>
            </div>
        </div>
    );
}